import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import { CreditCard, Wallet, MapPin, Lock, Receipt, User, Clock, Stethoscope, Sparkles, AlertTriangle, Info, Check, Loader2, ArrowLeft, Tag, Percent } from 'lucide-react';
import Input from '../ui/Input';
import { Checkbox } from '../ui/Checkbox';
import { appointmentService } from '../../services/appointmentService';
import { staffService } from '../../services/staffService';
import { wellnessService } from '../../services/wellnessService';
import { isSupabaseConfigured } from '../../lib/supabase';
import { calculateWellnessTotal, calculateConsultationTotal, formatPriceBreakdown } from '../../utils/pricingUtils';
import PaymentGateway from '../payment/PaymentGateway';

const UnifiedPaymentConfirmation = forwardRef(({ bookingData, onDataUpdate, onBack, bookingType = 'therapy', onValidationChange }, ref) => {
  console.log('🔄 UnifiedPaymentConfirmation RENDER');

  const [paymentMethod, setPaymentMethod] = useState('');
  const [cardDetails, setCardDetails] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: ''
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errors, setErrors] = useState({});
  const [priceBreakdown, setPriceBreakdown] = useState(null);
  const [loadingPrice, setLoadingPrice] = useState(true);

  // Payment-first flow states
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [showPaymentGateway, setShowPaymentGateway] = useState(false);

  // Ref for error message container to scroll to it when error occurs
  const errorRef = useRef(null);

  // Fetch pricing with discount and GST
  useEffect(() => {
    const fetchPricing = async () => {
      setLoadingPrice(true);
      try {
        let pricing;
        if (bookingType === 'consultation') {
          const consultationFee = bookingData?.doctor?.consultationFee || 500;
          pricing = await calculateConsultationTotal(consultationFee);
        } else {
          // Wellness booking
          pricing = await calculateWellnessTotal(bookingData?.services || []);
        }
        setPriceBreakdown(formatPriceBreakdown(pricing));
      } catch (error) {
        console.error('Error fetching pricing:', error);
        // Fallback to basic pricing
        const basePrice = bookingType === 'consultation'
          ? (bookingData?.doctor?.consultationFee || 500)
          : (bookingData?.services?.reduce((sum, s) => sum + (s?.base_price || 0), 0) || 0);
        setPriceBreakdown({
          basePrice: basePrice.toFixed(2),
          gstAmount: '0.00',
          gstPercentage: null,
          discountPercentage: '0.00',
          discountAmount: '0.00',
          discountReason: null,
          finalAmount: basePrice.toFixed(2),
          hasGst: false,
          hasDiscount: false,
          savings: '0.00'
        });
      } finally {
        setLoadingPrice(false);
      }
    };

    fetchPricing();
  }, [bookingType, bookingData?.doctor?.consultationFee, bookingData?.services]);

  // Expose handleConfirmBooking and validation to parent via ref
  useImperativeHandle(ref, () => ({
    handleConfirmBooking,
    canProceed: () => {
      // For online payment methods, payment must be completed first
      if (paymentMethod === 'upi' || paymentMethod === 'card') {
        return paymentCompleted && agreedToTerms === true;
      }
      // For pay at clinic, just need terms agreement
      return paymentMethod === 'clinic' && agreedToTerms === true;
    }
  }), [paymentMethod, agreedToTerms, paymentCompleted]);

  // RE-ENABLED - Check Supabase configuration on mount (SAFE - only runs once on mount)
  useEffect(() => {
    if (!isSupabaseConfigured()) {
      console.error('Supabase is not configured. Please check your .env file.');
      setErrors({ general: 'Database connection not configured. Please contact support.' });
    }
  }, []); // Empty dependency array = runs only once on mount

  // Notify parent when validation state changes (so button can enable/disable)
  useEffect(() => {
    if (onValidationChange) {
      const isValid = paymentMethod !== "" && agreedToTerms === true;
      onValidationChange(isValid);
    }
  }, [paymentMethod, agreedToTerms, onValidationChange]);





  const handleCardInputChange = (field, value) => {
    setCardDetails(prev => ({
      ...prev,
      [field]: value
    }));

    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  /**
   * Handle payment success
   */
  const handlePaymentSuccess = (paymentDetails) => {
    console.log('✅ Payment successful:', paymentDetails);
    setPaymentCompleted(true);
    setPaymentData(paymentDetails);
    setShowPaymentGateway(false);
    setErrors({});
  };

  /**
   * Handle payment failure
   */
  const handlePaymentFailure = (error) => {
    console.error('❌ Payment failed:', error);
    setPaymentCompleted(false);
    setPaymentData(null);
    setErrors({
      payment: error.message || 'Payment failed. Please try again.'
    });
  };

  /**
   * Handle payment cancellation
   */
  const handlePaymentCancel = (error) => {
    console.log('⚠️ Payment cancelled:', error);
    setPaymentCompleted(false);
    setPaymentData(null);
    setShowPaymentGateway(false);
  };

  const validatePayment = () => {
    const newErrors = {};

    // For online payment methods, payment must be completed first
    // Check for 'online', 'upi', or 'card' payment methods
    const isOnlinePayment = paymentMethod === 'online' || paymentMethod === 'upi' || paymentMethod === 'card';

    if (isOnlinePayment && !paymentCompleted) {
      newErrors.payment = 'Please complete the payment first before confirming your booking';
      console.error('❌ Validation failed: Payment not completed for online payment method');
    }

    // Additional validation: Ensure payment data exists for online payments
    if (isOnlinePayment && paymentCompleted && !paymentData) {
      newErrors.payment = 'Payment verification failed. Please try again.';
      console.error('❌ Validation failed: Payment completed but no payment data');
    }

    if (paymentMethod === 'card') {
      if (!cardDetails?.cardNumber?.replace(/\s/g, '')) {
        newErrors.cardNumber = 'Card number is required';
      } else if (cardDetails?.cardNumber?.replace(/\s/g, '')?.length < 16) {
        newErrors.cardNumber = 'Please enter a valid card number';
      }

      if (!cardDetails?.expiryDate) {
        newErrors.expiryDate = 'Expiry date is required';
      }

      if (!cardDetails?.cvv) {
        newErrors.cvv = 'CVV is required';
      } else if (cardDetails?.cvv?.length < 3) {
        newErrors.cvv = 'Please enter a valid CVV';
      }

      if (!cardDetails?.cardholderName?.trim()) {
        newErrors.cardholderName = 'Cardholder name is required';
      }
    }

    if (!agreedToTerms) {
      newErrors.terms = 'Please agree to the terms and conditions';
    }

    setErrors(newErrors);

    // Log validation result
    if (Object.keys(newErrors).length > 0) {
      console.error('❌ Validation errors:', newErrors);
    } else {
      console.log('✅ Validation passed');
    }

    return Object.keys(newErrors)?.length === 0;
  };

  const handleConfirmBooking = async () => {
    console.log(`🚀 === Confirm ${bookingType === 'consultation' ? 'Consultation' : 'Therapy'} Booking Started ===`);
    console.log('Booking Data:', bookingData);
    console.log('Payment Method:', paymentMethod);
    console.log('Agreed to Terms:', agreedToTerms);

    // Prevent multiple submissions
    if (isProcessing) {
      console.log('⚠️ Already processing, ignoring duplicate call');
      throw new Error('Already processing booking');
    }

    if (!validatePayment()) {
      console.log('❌ Validation failed - throwing error to prevent navigation');
      throw new Error('Validation failed. Please check all required fields.');
    }

    setIsProcessing(true);
    setErrors({}); // Clear previous errors only after validation passes

    try {
      // Check Supabase configuration
      if (!isSupabaseConfigured()) {
        throw new Error('Database connection not configured');
      }

      // Prepare appointment data based on booking type
      const finalAmount = priceBreakdown ? parseFloat(priceBreakdown.finalAmount) : 0;
      const basePrice = priceBreakdown ? parseFloat(priceBreakdown.basePrice) : 0;
      const gstAmount = priceBreakdown ? parseFloat(priceBreakdown.gstAmount) : 0;
      const discountAmount = priceBreakdown ? parseFloat(priceBreakdown.discountAmount) : 0;

      const appointmentData = {
        details: bookingData?.details,
        dateTime: bookingData?.dateTime,
        payment: {
          method: paymentMethod,
          status: paymentMethod === 'clinic' ? 'pending' : 'paid',
          // Include Razorpay payment details if payment was completed
          razorpay_payment_id: paymentData?.paymentId || null,
          razorpay_order_id: paymentData?.orderId || null,
          transaction_id: paymentData?.paymentId || null,
          amount: finalAmount,
          basePrice: basePrice,
          gstAmount: gstAmount,
          discountAmount: discountAmount,
          discountReason: priceBreakdown?.discountReason || null
        }
      };

      // Add booking-type-specific fields
      if (bookingType === 'consultation') {
        appointmentData.doctor = bookingData?.doctor;
        appointmentData.treatmentId = bookingData?.treatmentId;
        appointmentData.payment.consultationFee = basePrice;
      } else {
        // Therapy booking - need to select an available therapist
        appointmentData.services = bookingData?.services;
        appointmentData.treatmentFee = finalAmount; // Final amount after discount and GST

        // Calculate total duration for therapist availability check
        const selectedServices = bookingData?.services || [];
        const totalDuration = wellnessService.calculateTotalDuration(selectedServices);

        console.log('🔍 Finding available therapist for therapy booking...');
        console.log('  📅 Date:', bookingData?.dateTime?.date);
        console.log('  ⏰ Time:', bookingData?.dateTime?.timeSlot);
        console.log('  👤 Gender:', bookingData?.details?.gender);
        console.log('  ⏱️ Duration:', totalDuration, 'minutes');

        // Get available therapists for the selected date/time
        const formattedDate = new Date(bookingData?.dateTime?.date)?.toISOString()?.split('T')[0];
        const formattedTime = bookingData?.dateTime?.timeSlot?.includes(':') && bookingData?.dateTime?.timeSlot?.split(':')?.length === 2
          ? `${bookingData?.dateTime?.timeSlot}:00`
          : bookingData?.dateTime?.timeSlot;

        const availableTherapists = await staffService?.getAvailableTherapistsForTherapy(
          formattedDate,
          formattedTime,
          bookingData?.details?.gender,
          totalDuration,
          1 // We only need 1 therapist
        );

        console.log('  📦 Available therapists:', availableTherapists);

        if (!availableTherapists || availableTherapists?.length === 0) {
          throw new Error('No therapists available for the selected time slot. Please choose a different time.');
        }

        // Select the first available therapist
        const selectedTherapist = availableTherapists[0];
        console.log('  ✅ Selected therapist:', selectedTherapist);

        // Add therapist to appointment data in the format expected by appointmentService
        appointmentData.therapist = {
          id: selectedTherapist?.staffId,
          fullName: selectedTherapist?.fullName,
          gender: selectedTherapist?.gender
        };

        console.log('  📝 Therapist added to appointment data');
      }

      console.log('Creating appointment with data:', appointmentData);

      // Create appointment and client record
      const result = await appointmentService?.createAppointment(appointmentData);
      console.log('Appointment created:', result);

      if (!result || !result.appointment) {
        throw new Error('Failed to create appointment - no result returned');
      }

      const { appointment, client } = result;

      // Send WhatsApp notification
      if (appointment && client) {
        console.log('Sending WhatsApp notification...');
        const notificationData = {
          client: client,
          dateTime: bookingData?.dateTime,
        };

        if (bookingType === 'consultation') {
          notificationData.doctor = bookingData?.doctor;
          notificationData.treatment = { title: bookingData?.treatmentName };
          notificationData.consultationFee = finalAmount;
        } else {
          notificationData.services = bookingData?.services;
          notificationData.totalAmount = finalAmount;
        }

        await appointmentService?.sendWhatsAppNotification(appointment?.id, notificationData);
      }

      // Update booking data with appointment ID and payment information
      onDataUpdate({
        appointmentId: appointment?.id,
        clientId: client?.id,
        payment: appointmentData?.payment
      });

      console.log(`✅ === ${bookingType === 'consultation' ? 'Consultation' : 'Therapy'} Booking Confirmed Successfully ===`);

      // DON'T call onNext() here - the parent will move to next step after this function completes
      // Calling onNext() here causes infinite loop because onNext is handleNext() which calls this function again!
    } catch (error) {
      console.error('=== Error creating appointment ===');
      console.error('Error details:', error);
      console.error('Error message:', error?.message);
      console.error('Error stack:', error?.stack);

      // Parse error message for better user feedback
      let errorMessage = 'Failed to create appointment. Please try again.';

      if (error?.message) {
        // Check for post-natal gender validation error (highest priority)
        if (error.message.includes('Post-natal services are only available for female patients')) {
          errorMessage = error.message; // Use the exact error message from backend
        }
        // Check for specific constraint violations
        else if (error.message.includes('clients_email_check') || error.message.includes('clients_phone_check')) {
          errorMessage = 'Invalid email or phone number format. Please check your contact details.';
        } else if (error.message.includes('violates check constraint')) {
          errorMessage = 'This time slot is no longer available. Please select a different time.';
        } else if (error.message.includes('duplicate') || error.message.includes('unique')) {
          errorMessage = 'You already have an appointment at this time. Please select a different slot.';
        } else if (error.message.includes('foreign key')) {
          errorMessage = bookingType === 'consultation' ? 'Invalid doctor or treatment selection. Please try again.' : 'Invalid service selection. Please try again.';
        } else {
          // Use the original error message if it's user-friendly
          errorMessage = error.message;
        }
      }

      setErrors({
        general: errorMessage
      });

      // Scroll to error message so user can see it
      setTimeout(() => {
        if (errorRef.current) {
          errorRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }, 100);

      // CRITICAL: Re-throw the error to prevent navigation to success page
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  const formatCardNumber = (value) => {
    if (!value) return '';
    const v = value?.replace(/\s+/g, '')?.replace(/[^0-9]/gi, '');
    const matches = v?.match(/\d{4,16}/g);
    const match = matches && matches?.[0] || '';
    const parts = [];
    for (let i = 0, len = match?.length; i < len; i += 4) {
      parts?.push(match?.substring(i, i + 4));
    }
    if (parts?.length) {
      return parts?.join(' ');
    } else {
      return v;
    }
  };

  const formatExpiryDate = (value) => {
    if (!value) return '';
    const v = value?.replace(/\s+/g, '')?.replace(/[^0-9]/gi, '');
    if (v?.length >= 2) {
      return v?.substring(0, 2) + '/' + v?.substring(2, 4);
    }
    return v;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date?.toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="font-headline text-2xl font-semibold mb-2 text-purple-800">Payment & Confirmation</h2>
        <p className="text-gray-600">
          Complete your {bookingType === 'consultation' ? 'consultation' : 'therapy'} booking
        </p>
      </div>

      {/* Error Display */}
      {errors?.general && (
        <div
          ref={errorRef}
          className="bg-red-50 border border-red-200 rounded-lg p-4"
        >
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-800">{errors?.general}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Payment Section */}
        <div className="lg:col-span-2 space-y-6">
          {/* Payment Method Selection */}
          <div className="glass-panel p-6 rounded-xl border border-purple-200">
            <h3 className="font-semibold text-lg mb-4 flex items-center text-purple-800">
              <CreditCard className="w-5 h-5 mr-2" />
              Payment Method
            </h3>

            <div className="space-y-3 mb-6">
              {/* Pay at Clinic Option */}
              <label className="flex items-start cursor-pointer p-4 rounded-lg border border-gray-300 hover:bg-purple-50 transition-colors">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="clinic"
                  checked={paymentMethod === 'clinic'}
                  onChange={(e) => {
                    setPaymentMethod(e?.target?.value);
                    setPaymentCompleted(false);
                    setPaymentData(null);
                    setShowPaymentGateway(false);
                  }}
                  className="sr-only"
                />
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-3 mt-0.5 transition-all duration-200 ${paymentMethod === 'clinic' ? 'bg-purple-600 border-purple-600' : 'border-gray-400'
                  }`}>
                  {paymentMethod === 'clinic' && (
                    <div className="w-2 h-2 bg-white rounded-full" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center">
                    <MapPin className="w-5 h-5 mr-2 text-gray-600" />
                    <span className="font-medium text-gray-800">Pay at Clinic</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Pay when you arrive for your {bookingType === 'consultation' ? 'consultation' : 'appointment'}
                  </p>
                </div>
              </label>
              {/* Temporarily hiding online payment option until Razorpay approvals */}
              {/* Online Payment Option */}
              {/* <label className="flex items-start cursor-pointer p-4 rounded-lg border border-gray-300 hover:bg-purple-50 transition-colors">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="online"
                  checked={paymentMethod === 'online' || paymentMethod === 'upi' || paymentMethod === 'card'}
                  onChange={(e) => {
                    console.log('🔄 Switching to online payment - resetting payment state');
                    setPaymentMethod('online');
                    setShowPaymentGateway(true);
                    // CRITICAL FIX: Reset payment state when switching to online payment
                    // This ensures previous failed/successful payments don't interfere
                    setPaymentCompleted(false);
                    setPaymentData(null);
                    setErrors({});
                  }}
                  className="sr-only"
                />
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-3 mt-0.5 transition-all duration-200 ${(paymentMethod === 'online' || paymentMethod === 'upi' || paymentMethod === 'card')
                  ? 'bg-purple-600 border-purple-600'
                  : 'border-gray-400'
                  }`}>
                  {(paymentMethod === 'online' || paymentMethod === 'upi' || paymentMethod === 'card') && (
                    <div className="w-2 h-2 bg-white rounded-full" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center">
                    <CreditCard className="w-5 h-5 mr-2 text-gray-600" />
                    <span className="font-medium text-gray-800">Pay Online</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Secure payment via UPI, Card, or Net Banking
                  </p>
                </div>
              </label> */}
            </div>

            {/* Payment Gateway Integration */}
            {showPaymentGateway && !paymentCompleted && priceBreakdown && (
              <div className="mt-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
                <PaymentGateway
                  amount={parseFloat(priceBreakdown.finalAmount)}
                  currency="INR"
                  customerInfo={{
                    name: bookingData?.details?.fullName || '',
                    email: bookingData?.details?.email || '',
                    phone: bookingData?.details?.phone || ''
                  }}
                  orderDetails={{
                    description: bookingType === 'consultation'
                      ? `Consultation with ${bookingData?.doctor?.name || 'Doctor'}`
                      : `Wellness Therapy - ${bookingData?.services?.map(s => s.title || s.name).join(', ')}`,
                    bookingType: bookingType
                  }}
                  onSuccess={handlePaymentSuccess}
                  onFailure={handlePaymentFailure}
                  onCancel={handlePaymentCancel}
                  disabled={isProcessing}
                />
              </div>
            )}

            {/* Payment Success Message */}
            {paymentCompleted && paymentData && (
              <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-3">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-green-800">Payment Successful!</p>
                    <p className="text-sm text-green-600 mt-1">
                      Payment ID: {paymentData.paymentId}
                    </p>
                    <p className="text-sm text-green-600">
                      Amount: ₹{paymentData.amount}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Error Message */}
            {errors?.payment && (
              <div className="mt-6 p-4 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-center">
                  <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
                  <p className="text-red-800">{errors.payment}</p>
                </div>
              </div>
            )}
          </div>

          {/* Pay at Clinic */}
          {paymentMethod === 'clinic' && (
            <div className="glass-panel p-6 rounded-xl border border-purple-200">
              <div className="text-center py-8">
                <MapPin className="w-12 h-12 mx-auto mb-4 text-purple-600" />
                <h3 className="font-semibold text-lg mb-2 text-purple-800">Pay at Clinic</h3>
                <p className="text-gray-600 mb-4">
                  Complete your payment when you arrive for your {bookingType === 'consultation' ? 'consultation' : 'appointment'}.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-amber-800 text-sm flex items-center justify-center">
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Please arrive 15 minutes early to complete payment and check-in.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* UPI Payment */}
          {paymentMethod === 'upi' && (
            <div className="glass-panel p-6 rounded-xl border border-purple-200">
              <div className="text-center py-8">
                <Wallet className="w-12 h-12 mx-auto mb-4 text-purple-600" />
                <h3 className="font-semibold text-lg mb-2 text-purple-800">UPI Payment</h3>
                <p className="text-gray-600 mb-4">
                  You will receive a UPI payment link via WhatsApp after booking confirmation.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-blue-800 text-sm flex items-center justify-center">
                    <Info className="w-4 h-4 mr-2" />
                    UPI payment link will be sent to your registered mobile number.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Terms and Conditions */}
          <div className="glass-panel p-6 rounded-xl border border-purple-200">
            <div className="space-y-4">
              <Checkbox
                label="I agree to the Terms and Conditions and Privacy Policy"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e?.target?.checked)}
                error={errors?.terms}
                required
              />

              <div className="text-sm text-gray-600 space-y-2">
                <p>• Cancellations must be made at least 24 hours in advance</p>
                <p>• Late arrivals may result in shortened {bookingType === 'consultation' ? 'consultation' : 'service'} time</p>
                <p>• Full payment is required for no-shows</p>
                <p>• Rescheduling is subject to {bookingType === 'consultation' ? 'doctor' : 'therapist'} availability</p>
              </div>
            </div>
          </div>
        </div>

        {/* Booking Summary */}
        <div className="lg:col-span-1">
          <div className="glass-panel p-6 rounded-xl border border-purple-200 sticky top-8">
            <h3 className="font-semibold text-lg mb-4 flex items-center text-purple-800">
              <Receipt className="w-5 h-5 mr-2" />
              {bookingType === 'consultation' ? 'Consultation Summary' : 'Booking Summary'}
            </h3>

            {/* Patient/Client Info */}
            <div className="mb-6">
              <div className="flex items-center mb-2">
                <User className="w-4 h-4 mr-2 text-purple-600" />
                <span className="font-medium text-gray-800">{bookingType === 'consultation' ? 'Patient' : 'Client'}</span>
              </div>
              <p className="text-sm text-gray-600 ml-6">
                {bookingData?.details?.name}
              </p>
              <p className="text-sm text-gray-600 ml-6">
                {bookingData?.details?.email}
              </p>
              <p className="text-sm text-gray-600 ml-6">
                {bookingData?.details?.phone}
              </p>
            </div>

            {/* Doctor (Consultation only) */}
            {bookingType === 'consultation' && bookingData?.doctor && (
              <div className="mb-6">
                <div className="flex items-center mb-2">
                  <Stethoscope className="w-4 h-4 mr-2 text-purple-600" />
                  <span className="font-medium text-gray-800">Doctor</span>
                </div>
                <p className="text-sm text-gray-600 ml-6">
                  {bookingData?.doctor?.fullName ? `Dr. ${bookingData?.doctor?.fullName}` : 'Dr.'}
                </p>
                {bookingData?.doctor?.specializations && bookingData?.doctor?.specializations?.length > 0 && (
                  <p className="text-xs text-gray-500 ml-6 mt-1">
                    {bookingData?.doctor?.specializations?.[0]}
                  </p>
                )}
              </div>
            )}

            {/* Services (Therapy only) */}
            {bookingType === 'therapy' && bookingData?.services && bookingData?.services?.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center mb-2">
                  <Sparkles className="w-4 h-4 mr-2 text-purple-600" />
                  <span className="font-medium text-gray-800">Services</span>
                </div>
                <div className="space-y-2">
                  {bookingData?.services?.map((service) => (
                    <div key={service?.id} className="flex items-center justify-between ml-6">
                      <span className="text-sm text-gray-600">{service?.name}</span>
                      <span className="text-sm font-semibold text-gray-800">₹{service?.base_price?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Date & Time */}
            <div className="mb-6">
              <div className="flex items-center mb-2">
                <Clock className="w-4 h-4 mr-2 text-purple-600" />
                <span className="font-medium text-gray-800">Appointment</span>
              </div>
              <p className="text-sm text-gray-600 ml-6">
                {formatDate(bookingData?.dateTime?.date)}
              </p>
              <p className="text-sm text-gray-600 ml-6">
                {bookingData?.dateTime?.timeSlot}
              </p>
            </div>

            {/* Chief Complaint (Consultation only) */}
            {bookingType === 'consultation' && bookingData?.details?.chiefComplaint && (
              <div className="mb-6">
                <div className="flex items-center mb-2">
                  <Info className="w-4 h-4 mr-2 text-purple-600" />
                  <span className="font-medium text-gray-800">Chief Complaint</span>
                </div>
                <p className="text-sm text-gray-600 ml-6">
                  {bookingData?.details?.chiefComplaint}
                </p>
              </div>
            )}

            {/* Pricing Breakdown */}
            <div className="border-t border-gray-200 pt-4 space-y-3">
              {loadingPrice ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
                  <span className="ml-2 text-sm text-gray-600">Calculating price...</span>
                </div>
              ) : priceBreakdown ? (
                <>
                  {/* Base Price */}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{bookingType === 'consultation' ? 'Consultation Fee' : 'Base Price'}</span>
                    <span className="text-gray-800">₹{priceBreakdown.basePrice}</span>
                  </div>

                  {/* GST (if applicable) */}
                  {priceBreakdown.hasGst && (
                    <div className="flex items-center justify-between text-sm bg-blue-50 p-2 rounded-lg">
                      <div className="flex items-center">
                        <Tag className="w-4 h-4 mr-2 text-blue-600" />
                        <span className="text-gray-600">GST ({priceBreakdown.gstPercentage}%)</span>
                      </div>
                      <span className="text-gray-800 font-medium">₹{priceBreakdown.gstAmount}</span>
                    </div>
                  )}

                  {/* Discount (if applicable) */}
                  {priceBreakdown.hasDiscount && (
                    <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <div className="flex items-center">
                          <Percent className="w-4 h-4 mr-2 text-green-600" />
                          <span className="text-green-700 font-medium">Discount ({priceBreakdown.discountPercentage}%)</span>
                        </div>
                        <span className="text-green-700 font-semibold">-₹{priceBreakdown.discountAmount}</span>
                      </div>
                      {priceBreakdown.discountReason && (
                        <p className="text-xs text-green-600 ml-6">{priceBreakdown.discountReason}</p>
                      )}
                    </div>
                  )}

                  {/* Savings Badge */}
                  {priceBreakdown.hasDiscount && parseFloat(priceBreakdown.savings) > 0 && (
                    <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white p-2 rounded-lg text-center">
                      <p className="text-sm font-semibold">🎉 You save ₹{priceBreakdown.savings}!</p>
                    </div>
                  )}

                  {/* Final Total */}
                  <div className="flex items-center justify-between text-lg font-semibold border-t border-gray-200 pt-3">
                    <span className="text-gray-800">Total Payable</span>
                    <span className="text-purple-600">₹{priceBreakdown.finalAmount}</span>
                  </div>
                </>
              ) : (
                <div className="text-center py-4 text-sm text-gray-500">
                  Unable to calculate pricing
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

UnifiedPaymentConfirmation.displayName = 'UnifiedPaymentConfirmation';

export default UnifiedPaymentConfirmation;

